/**
 * The shop's decision rules — ONE definition of each, used everywhere.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * These rules used to live twice: once in the accounting overview's Attention
 * Center and once in the Stock Doctor tab, with different constants. On the
 * same page, on 2026-09-04, the live shop was told BOTH of these:
 *
 *     45-day rule  →  121 products frozen, ₾3,672.70
 *     60-day rule  →   12 products frozen, ₾511.00
 *
 * Neither was a bug on its own. Having both on one screen was. An owner cannot
 * act on a number that changes when they switch tabs, and the whole direction
 * of this product — tell the owner what to DO, not just what happened — rests
 * on those numbers being trustworthy. A one-click "reorder these" button built
 * on two disagreeing engines just makes the wrong order arrive faster.
 *
 * So: every threshold is declared once, here, and every rule is a pure
 * function of data that is already in the store. No React, no store import,
 * no I/O — that is what makes them testable, and `npm test` covers them.
 *
 * Rules for changing anything in this file:
 *  1. A threshold changes here or nowhere. Never re-declare one in a component.
 *  2. Whatever number a rule uses must be SHOWN to the owner in the UI
 *     („60 დღეზე მეტი"), so nobody has to read code to know what they are
 *     looking at.
 *  3. Add a test with the change. These numbers drive money decisions.
 */

import { isService } from './service'
import type { Product, Sale, SaleItem } from './mock-data'
import type { AccountingIndex } from './accounting-index'
import { unitMargin } from './profit'
import { roundWeight } from './weight'

// ── Thresholds ───────────────────────────────────────────────────────────────
// Chosen deliberately, not inherited. See the note on DEAD_DAYS.

/** Window used to measure how fast something sells. */
export const VELOCITY_DAYS = 30

/**
 * No sale in this long, while holding stock = money standing still.
 *
 * 60 rather than 45, decided 2026-09-04. A product that arrived 45 days ago
 * has not "failed to sell" — it has not had a fair chance, and on the first
 * real shop that difference was 109 products and ₾3,161. The stricter window
 * is the one an owner can trust enough to act on.
 */
export const DEAD_DAYS = 60

/** A suggested order aims to cover this many days of demand. */
export const COVER_DAYS = 30

/** Days of remaining cover below which a seller is worth reordering now. */
export const REORDER_AT_DAYS = 45

/** Days ahead we start warning about a best-before date (migration 049). */
export const EXPIRY_SOON = 14

/** Days ahead the owner's once-a-day window counts (1.61, lib/expiry.ts). */
export const EXPIRY_WINDOW = 7

/** At or below this quantity a product counts as running low. */
export const LOW_STOCK = 5

/** Below this fraction, a margin is too thin to survive a cost rise. */
export const THIN_MARGIN = 0.10

const DAY = 86_400_000

// ── Movement: one pass over sales, reused by every rule ───────────────────────

export type Movement = {
  /** Net units sold in the velocity window (returns subtract). */
  sold: number
  /** Timestamp of the most recent real SALE, or null if never sold. */
  lastSold: number | null
}

/**
 * Per-product sales movement.
 *
 * Two judgement calls are baked in, and both must stay:
 *
 *  - **A return is negative movement.** Counting it as a sale would overstate
 *    how fast a shelf empties and produce phantom reorders.
 *  - **Only the active branch counts** when the org has more than one, because
 *    `products[].quantity` is already that branch's stock. Mixing org-wide
 *    sales with one branch's stock invents shortages that do not exist.
 */
export function movement(
  sales: Sale[],
  saleItems: SaleItem[],
  opts: { branchId?: string | null; multiBranch?: boolean; now?: number } = {},
): Map<string, Movement> {
  const now = opts.now ?? Date.now()
  const since = now - VELOCITY_DAYS * DAY
  const scoped = !!opts.multiBranch && !!opts.branchId

  const saleById = new Map(sales.map((s) => [s.id, s]))
  const acc = new Map<string, Movement>()

  for (const it of saleItems) {
    if (!it.product_id) continue
    const s = saleById.get(it.sale_id)
    if (!s) continue
    if (scoped && s.branch_id && s.branch_id !== opts.branchId) continue

    const t = new Date(s.created_at).getTime()
    const qty = s.type === 'return' ? -it.quantity : it.quantity

    const cur = acc.get(it.product_id) ?? { sold: 0, lastSold: null }
    if (t > since) cur.sold = roundWeight(cur.sold + qty)   // 1.72: to the gram, a kg product's lines read 0.30000000000000004
    if (qty > 0 && (cur.lastSold === null || t > cur.lastSold)) cur.lastSold = t
    acc.set(it.product_id, cur)
  }
  return acc
}

/** The accounting overview's movement path: identical rules, with sale
 * identity and parsed timestamps read from the shared accounting index. */
export function movementFromIndex(
  saleItems: SaleItem[],
  index: Pick<AccountingIndex, 'saleById' | 'timeOf'>,
  opts: { branchId?: string | null; multiBranch?: boolean; now?: number } = {},
): Map<string, Movement> {
  const now = opts.now ?? Date.now()
  const since = now - VELOCITY_DAYS * DAY
  const scoped = !!opts.multiBranch && !!opts.branchId
  const acc = new Map<string, Movement>()

  for (const item of saleItems) {
    if (!item.product_id) continue
    const sale = index.saleById.get(item.sale_id)
    if (!sale) continue
    if (scoped && sale.branch_id && sale.branch_id !== opts.branchId) continue

    const time = index.timeOf(item.sale_id)
    const quantity = (sale.type === 'return' ? -1 : 1) * item.quantity
    const current = acc.get(item.product_id) ?? { sold: 0, lastSold: null }
    if (time > since) current.sold = roundWeight(current.sold + quantity)
    if (quantity > 0 && (current.lastSold === null || time > current.lastSold)) current.lastSold = time
    acc.set(item.product_id, current)
  }
  return acc
}

// ── Rules ────────────────────────────────────────────────────────────────────

export type ReorderRow = {
  id: string; name: string; unit: string
  stock: number; sold: number; daysLeft: number; suggest: number
}

/** Sells, and will run out — soonest first. */
export function toReorder(products: Product[], mv: Map<string, Movement>): ReorderRow[] {
  const out: ReorderRow[] = []
  for (const p of products) {
    if (isService(p)) continue // 169 — a service never runs out
    const sold = mv.get(p.id)?.sold ?? 0
    if (sold <= 0) continue // never sold in the window — that is dead stock, not a reorder
    const perDay = sold / VELOCITY_DAYS
    const daysLeft = perDay > 0 ? p.quantity / perDay : Infinity
    if (daysLeft > REORDER_AT_DAYS) continue // comfortably stocked
    out.push({
      id: p.id, name: p.name, unit: p.unit, stock: p.quantity, sold, daysLeft,
      suggest: Math.max(1, Math.ceil(perDay * COVER_DAYS - p.quantity)),
    })
  }
  return out.sort((a, b) => a.daysLeft - b.daysLeft)
}

export type DeadRow = { id: string; name: string; qty: number; value: number; lastSold: number | null }

/** Holds stock, has not sold in DEAD_DAYS — money standing still. `days`
 *  answers an owner who asks about another window („30 დღეში", the AI
 *  manager's question, 2026-09-30); every screen keeps the rule's DEAD_DAYS. */
export function deadStock(
  products: Product[], mv: Map<string, Movement>, now = Date.now(), days = DEAD_DAYS,
): DeadRow[] {
  const cutoff = now - days * DAY
  const out: DeadRow[] = []
  for (const p of products) {
    if (p.quantity <= 0 || isService(p)) continue
    // A product added last week has not "failed to sell" — it has not had the
    // chance. Judging it would put every new arrival on this list.
    if (new Date(p.created_at).getTime() > cutoff) continue
    const last = mv.get(p.id)?.lastSold ?? null
    if (last !== null && last > cutoff) continue
    out.push({ id: p.id, name: p.name, qty: p.quantity, value: p.quantity * p.purchase_price, lastSold: last })
  }
  return out.sort((a, b) => b.value - a.value)
}

export type MarginRow = { id: string; name: string; cost: number; price: number; margin: number }

/** Priced at or below cost, or on a margin too thin to survive a cost rise. */
export function marginRisk(products: Product[]): MarginRow[] {
  const out: MarginRow[] = []
  for (const p of products) {
    if (p.purchase_price <= 0 || p.sale_price <= 0) continue
    const margin = unitMargin(p.sale_price, p.purchase_price) ?? 0
    if (margin >= THIN_MARGIN) continue
    out.push({ id: p.id, name: p.name, cost: p.purchase_price, price: p.sale_price, margin: margin * 100 })
  }
  return out.sort((a, b) => a.margin - b.margin)
}

/** Selling below cost outright — always a mistake worth its own alert. */
export function belowCost(products: Product[]): Product[] {
  return products.filter(
    (p) => p.purchase_price > 0 && p.sale_price > 0 && p.sale_price < p.purchase_price,
  )
}

// 1.61: expiry is per batch now — lib/expiry.ts (expiringLots, ownerWindow, expiredLotFor).

export const outOfStock = (products: Product[]) => products.filter((p) => p.quantity === 0 && !isService(p))
export const lowStock = (products: Product[]) =>
  products.filter((p) => p.quantity > 0 && p.quantity <= LOW_STOCK && !isService(p))

/** Total value of a set of rows carrying a `value`. */
export const sumValue = (rows: { value: number }[]) => rows.reduce((s, r) => s + r.value, 0)
